import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pluginsAPI } from '@/utils/api';
import useAppStore from '@/store/appStore';

export function usePlugins() {
  return useQuery({
    queryKey: ['plugins'],
    queryFn: async () => {
      const response = await pluginsAPI.getAll();
      return response.data;
    },
  });
}

export function usePluginStatus() {
  return useQuery({
    queryKey: ['plugins', 'status'],
    queryFn: async () => {
      const response = await pluginsAPI.getStatus();
      return response.data;
    },
  });
}

export function usePluginStats() {
  return useQuery({
    queryKey: ['plugins', 'stats'],
    queryFn: async () => {
      const response = await pluginsAPI.getStats();
      return response.data;
    },
  });
}

export function useEnablePlugin() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (name) => pluginsAPI.enable(name),
    onSuccess: (_, name) => {
      queryClient.invalidateQueries({ queryKey: ['plugins'] });
      addNotification({
        type: 'success',
        title: 'Plugin Enabled',
        message: `${name} has been enabled`,
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Enable Plugin',
        message: error.message,
      });
    },
  });
}

export function useDisablePlugin() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (name) => pluginsAPI.disable(name),
    onSuccess: (_, name) => {
      queryClient.invalidateQueries({ queryKey: ['plugins'] });
      addNotification({
        type: 'success',
        title: 'Plugin Disabled',
        message: `${name} has been disabled`,
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Disable Plugin',
        message: error.message,
      });
    },
  });
}

export function usePluginData(emailId) {
  return useQuery({
    queryKey: ['plugins', 'data', emailId],
    queryFn: async () => {
      const response = await pluginsAPI.getData(emailId);
      return response.data;
    },
    enabled: !!emailId,
  });
}

export function useReloadPlugins() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: () => pluginsAPI.reload(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['plugins'] });
      const count = response.data?.plugins?.length || 0;
      addNotification({
        type: 'success',
        title: 'Plugins Reloaded',
        message: `${count} plugin(s) loaded successfully`,
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Reload Plugins',
        message: error.message,
      });
    },
  });
}